import axios from '../../api/axios';

/**
 * API endpoints for inventory order operations
 */
export const ENDPOINTS = {
  GET_ORDERS: 'v2/inventory-orders/orders',
  GET_MRO_SUPPLIERS: 'v2/inventory-orders/suppliers',
  GET_INVENTORY_ITEMS: 'v2/inventory-orders/items',
  CREATE_ORDER: 'v2/inventory-orders/create',
  UPDATE_ORDER: 'v2/inventory-orders/update/:id',
};

/**
 * Centralized error handler for API calls
 * @param {Error} error - Axios error object
 * @param {string} defaultMessage - Default error message
 */
const handleApiError = (error, defaultMessage) => {
  const message = error.response?.data?.message || error.message || defaultMessage;
  console.error(defaultMessage, error);
  throw new Error(message);
};

/**
 * Fetches inventory orders for a property and building
 * @param {string} property_id - Property ID
 * @param {string} building_name - Building name
 * @returns {Promise<Object>} Inventory orders data
 */
export const getOrders = async (property_id, building_name) => {
  try {
    const response = await axios.get(ENDPOINTS.GET_ORDERS, {
      params: { property_id, building_name },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch orders');
  }
};

/**
 * Fetches active MRO suppliers
 * @returns {Promise<Object>} MRO suppliers data
 */
export const getMROSuppliers = async () => {
  try {
    const response = await axios.get(ENDPOINTS.GET_MRO_SUPPLIERS);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch MRO suppliers');
  }
};

/**
 * Fetches inventory items for a property and building
 * @param {string} property_id - Property ID
 * @param {string} building_name - Building name
 * @returns {Promise<Object>} Inventory items data
 */
export const getInventoryItems = async (property_id, building_name) => {
  try {
    const response = await axios.get(ENDPOINTS.GET_INVENTORY_ITEMS, {
      params: { property_id, building_name },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch inventory items');
  }
};

/**
 * Creates a new inventory order
 * @param {Object} orderData - Order data
 * @returns {Promise<Object>} Created order data
 */
export const createOrder = async (orderData) => {
  try {
    const response = await axios.post(ENDPOINTS.CREATE_ORDER, orderData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to create order');
  }
};

/**
 * Updates an existing inventory order
 * @param {string} orderId - Order ID
 * @param {Object} updateData - Updated order data
 * @returns {Promise<Object>} Updated order data
 */
export const updateOrder = async (orderId, updateData) => {
  try {
    const url = ENDPOINTS.UPDATE_ORDER.replace(':id', orderId);
    const response = await axios.put(url, updateData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to update order');
  }
};